/**
 * Pulse Animation
 * Creates breathing/heartbeat effects on components
 */
import { AnimationEngine } from './AnimationEngine';
import { PulseConfig, AnimationTarget } from './animationTypes';

export class PulseAnimation {
    private engine: AnimationEngine;
    private originalValues: Map<string, { scale: number; opacity: number }> = new Map();

    constructor(engine: AnimationEngine) {
        this.engine = engine;
    }

    /**
     * Start a pulse animation on a target
     */
    public startPulse(target: AnimationTarget, config: PulseConfig = {}): string {
        const {
            duration = 1000,
            scale = 1.1,
            minOpacity = 0.7,
            pulseColor,
            repeat = -1, // Infinite by default
            easing = 'easeInOutSine',
            ...restConfig
        } = config;

        // Store original values
        const targetId = (target as any).id;
        if (!this.originalValues.has(targetId)) {
            this.originalValues.set(targetId, {
                scale: 1,
                opacity: 1
            });
        }

        // Create animation properties
        const properties: Record<string, number> = {};

        // Add scale animation for ViewItems
        if ('tile' in target) {
            properties['_pulseScale'] = scale;
        }

        // Add opacity animation if specified
        if (minOpacity < 1) {
            properties['_pulseOpacity'] = minOpacity;
        }

        // Start the animation
        return this.engine.animate(target, properties, {
            duration,
            easing,
            repeat,
            yoyo: true, // Pulse goes up and down
            onUpdate: (progress) => {
                // Update visual properties based on progress
                // This would typically update a store or emit events
                // that the renderer would respond to
                this.updatePulseVisuals(target, progress, scale, minOpacity);
            },
            onComplete: () => {
                this.resetTarget(targetId);
            },
            onStop: () => {
                this.resetTarget(targetId);
            },
            ...restConfig
        });
    }

    /**
     * Create a heartbeat effect (double pulse)
     */
    public startHeartbeat(target: AnimationTarget, config: PulseConfig = {}): string {
        const {
            duration = 1500,
            scale = 1.15,
            ...restConfig
        } = config;

        // Create a custom animation for heartbeat pattern
        const animationId = this.engine.animate(
            target,
            { _heartbeatProgress: 1 },
            {
                duration,
                repeat: -1,
                easing: 'linear',
                onUpdate: (progress) => {
                    // Create double-pulse pattern
                    let pulseScale = 1;

                    if (progress < 0.1) {
                        // First pulse up
                        pulseScale = 1 + (scale - 1) * (progress / 0.1);
                    } else if (progress < 0.2) {
                        // First pulse down
                        pulseScale = scale - (scale - 1) * ((progress - 0.1) / 0.1);
                    } else if (progress < 0.3) {
                        // Second pulse up
                        pulseScale = 1 + (scale - 1) * 0.8 * ((progress - 0.2) / 0.1);
                    } else if (progress < 0.4) {
                        // Second pulse down
                        pulseScale = 1 + (scale - 1) * 0.8 * (1 - (progress - 0.3) / 0.1);
                    }

                    this.updatePulseVisuals(target, 0, pulseScale, 1);
                },
                ...restConfig
            }
        );

        return animationId;
    }

    /**
     * Create a breathing effect (slow, smooth pulse)
     */
    public startBreathing(target: AnimationTarget, config: PulseConfig = {}): string {
        return this.startPulse(target, {
            duration: 3000,
            scale: 1.05,
            easing: 'easeInOutSine',
            repeat: -1,
            ...config
        });
    }

    /**
     * Create an attention-grabbing pulse
     */
    public startAttentionPulse(target: AnimationTarget, config: PulseConfig = {}): string {
        return this.startPulse(target, {
            duration: 500,
            scale: 1.2,
            repeat: 3,
            easing: 'easeOutElastic',
            ...config
        });
    }

    /**
     * Update visual properties during pulse
     */
    private updatePulseVisuals(
        target: AnimationTarget,
        progress: number,
        maxScale: number,
        minOpacity: number
    ): void {
        // This is where you would update the visual representation
        // In a real implementation, this would update the store or emit events

        // Example of what might happen:
        // - Update a '_pulseScale' property that the renderer reads
        // - Update an '_pulseOpacity' property
        // - Emit events that the canvas responds to
        console.debug(`Pulse progress: ${progress}, scale: ${maxScale}, opacity: ${minOpacity}`);
        // For now, we'll just store these values on the target
        (target as any)._pulseScale = maxScale;
        (target as any)._pulseOpacity = minOpacity;
    }

    /**
     * Reset target to original values
     */
    private resetTarget(targetId: string): void {
        const original = this.originalValues.get(targetId);
        if (original) {
            // Reset visual properties
            this.originalValues.delete(targetId);
        }
    }
}

/**
 * Create standalone pulse animations
 */
export function createPulseAnimation(
    engine: AnimationEngine,
    target: AnimationTarget,
    config?: PulseConfig
): string {
    const pulseAnim = new PulseAnimation(engine);
    return pulseAnim.startPulse(target, config);
}

export function createHeartbeatAnimation(
    engine: AnimationEngine,
    target: AnimationTarget,
    config?: PulseConfig
): string {
    const pulseAnim = new PulseAnimation(engine);
    return pulseAnim.startHeartbeat(target, config);
}

export function createBreathingAnimation(
    engine: AnimationEngine,
    target: AnimationTarget,
    config?: PulseConfig
): string {
    const pulseAnim = new PulseAnimation(engine);
    return pulseAnim.startBreathing(target, config);
}